import {
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getDateLocale, getWeekDays } from "@/components/atomic/calendar/calendarUtils";
import { DayButton } from "@/components/atomic/calendar/DayButton";
import { cn } from "@/lib/utils";

const COLOR_RANGE_FILL = "bg-[#afc5ff]";
const COLOR_RANGE_START = "bg-gradient-to-r from-transparent from-50% to-[#afc5ff] to-50%";
const COLOR_RANGE_END = "bg-gradient-to-l from-transparent from-50% to-[#afc5ff] to-50%";
const COLOR_SELECTED_BG = "bg-[#6888e1]";
const COLOR_TODAY_BORDER = "border border-[#cccccc]";
const COLOR_CURRENT_TEXT = "text-[#333333]";
const COLOR_DIM_TEXT = "text-[#DDDDDD]";
const STYLE_SELECTED_TEXT = "text-white";

const getDayClassName = (
  day: Date,
  currentMonth: Date,
  rangeStart: Date | null,
  rangeEnd: Date | null,
): string => {
  const isCurrentMonth = isSameMonth(day, currentMonth);
  const isDisabled = !isCurrentMonth;
  const isRangeStart = !!rangeStart && isSameDay(day, rangeStart);
  const isRangeEnd = !!rangeEnd && isSameDay(day, rangeEnd);
  const isInRange = !!rangeStart && !!rangeEnd && !isRangeStart && !isRangeEnd
    && isWithinInterval(day, { start: rangeStart, end: rangeEnd });
  const isSelected = isRangeStart || isRangeEnd;
  const isTodayDate = isToday(day);

  return cn(
    !isCurrentMonth && !isSelected && COLOR_DIM_TEXT,
    isCurrentMonth && !isDisabled && !isSelected && !isInRange && COLOR_CURRENT_TEXT,
    !isDisabled && "cursor-pointer",
    !isDisabled && !isSelected && !isInRange && "hover:bg-[#e0e7ff]",
    isTodayDate && !isSelected && COLOR_TODAY_BORDER,
    isSelected && cn(COLOR_SELECTED_BG, STYLE_SELECTED_TEXT),
    isInRange && !isDisabled && "text-white",
    isInRange && isDisabled && COLOR_DIM_TEXT,
  );
};

const getRangeClassName = (
  day: Date,
  currentMonth: Date,
  rangeStart: Date | null,
  rangeEnd: Date | null,
): string => {
  const isCurrentMonth = isSameMonth(day, currentMonth);
  if (!isCurrentMonth || !rangeStart || !rangeEnd || isSameDay(rangeStart, rangeEnd)) {
    return "";
  }

  const isRangeStart = isSameDay(day, rangeStart);
  const isRangeEnd = isSameDay(day, rangeEnd);
  const isInRange = !isRangeStart && !isRangeEnd
    && isWithinInterval(day, { start: rangeStart, end: rangeEnd });

  if (isRangeStart) return COLOR_RANGE_START;
  if (isRangeEnd) return COLOR_RANGE_END;
  if (isInRange) return COLOR_RANGE_FILL;
  return "";
};

interface MonthCalendarProps {
  currentMonth: Date;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  rangeStart: Date | null;
  rangeEnd: Date | null;
  onDateClick: (date: Date) => void;
  disablePrevMonth?: boolean;
  disableNextMonth?: boolean;
}

export const MonthCalendar = (props: Readonly<MonthCalendarProps>) => {
  const {
    currentMonth,
    onPrevMonth,
    onNextMonth,
    rangeStart,
    rangeEnd,
    onDateClick,
    disablePrevMonth = false,
    disableNextMonth = false,
  } = props;

  const { i18n } = useTranslation();
  const currentLocale = i18n.language;
  const dateLocale = getDateLocale(currentLocale);
  const weekDays = getWeekDays(currentLocale);

  const weeks = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
    const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
    const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    const result: Date[][] = [];
    for (let i = 0; i < allDays.length; i += 7) {
      result.push(allDays.slice(i, i + 7));
    }
    return result;
  }, [currentMonth]);

  return (
    <div className="flex flex-col gap-2 w-56">
      <div className="flex items-center justify-between h-5">
        <button onClick={onPrevMonth} disabled={disablePrevMonth}>
          <ChevronLeftIcon
            className={cn(
              "h-full w-5  rounded",
              disablePrevMonth
                ? "text-[#dddddd] cursor-not-allowed"
                : "text-[#737373] hover:bg-gray-100 cursor-pointer",
            )}
          />
        </button>
        <div className="font-semibold text-[14px] text-[#333333]">
          {format(currentMonth, currentLocale === "ja" ? "yyyy年 M月 " : "MMMM yyyy", { locale: dateLocale })}
        </div>
        <button onClick={onNextMonth} disabled={disableNextMonth}>
          <ChevronRightIcon
            className={cn(
              "h-full w-5 rounded",
              disableNextMonth
                ? "text-[#dddddd] cursor-not-allowed"
                : "text-[#737373] hover:bg-gray-100 cursor-pointer",
            )}
          />
        </button>
      </div>

      <div className="flex">
        {weekDays.map((day) => (
          <div
            key={day}
            className="w-8 h-6 flex items-center justify-center text-[14px] font-medium text-[#737373]"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-1">
        {weeks.map((week) => (
          <div key={week[0].toDateString()} className="flex h-8">
            {week.map((day) => {
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const dayClassName = getDayClassName(day, currentMonth, rangeStart, rangeEnd);
              const rangeClassName = getRangeClassName(day, currentMonth, rangeStart, rangeEnd);

              return (
                <div
                  key={day.toDateString()}
                  // 隙間ができないように調整
                  className={cn("w-8 h-8 scale-[1.02]", rangeClassName)}
                >
                  <DayButton
                    day={day}
                    className={dayClassName}
                    disabled={!isCurrentMonth}
                    onClick={() => onDateClick(day)}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
};
